import {
  StackedCell,
  StackedCellMainContent,
  StackedCellSubtitle,
  TableTitle,
} from 'components/Table/Table.styles';
import { ViewType } from '..';
import Tabs from 'components/Table/Tabs';
import SwitchTable from 'screens/Inventory/SwitchTable';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import Searchbar from 'components/Table/SearchBar';
import { useQueryState } from 'hooks/useQueryState';
import Customize from 'components/Table/Customize/Customize';
import { useCustomizeTHead } from 'hooks/useCustomizeTHead';
import { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';

import { useSortedTable } from 'hooks/useSortedTable';
import UploadWidgetTable from './UploadWidgetTable';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { useCallback, useState } from 'react';
import {
  ActionedInvoiceItem,
  ActionedInvoicesFilterSearchParams,
} from 'api/inventory/schema';
import { getActionedInvoicesIndex } from 'api/inventory/api';
import { setCookie, toSentenceCase } from 'shared/util/util';
import Tag from 'components/Tag';
import format from 'date-fns/format';
import Filters from 'components/Filters/Filters';
import { useFilterForTable } from 'hooks/useFilterForTable';
import { useQueryParams } from 'hooks/useQueryParams';
import { MultiValue, SingleValue } from 'react-select';
import React from 'react';
import useInventoryStaticData from 'hooks/useInventoryStaticData';
import { dateToUTCWithoutMS } from 'shared/util/dateToUtcStrWithoutMS';
import { startOfDay, subMonths, subWeeks, subYears } from 'date-fns';
import { Chip } from 'components/Chip/Chip';
import { formatBandwidth } from 'screens/SelectServices/utils';
import { InvoiceDownloadButton } from './InvoiceDownloadButton';

const SERVICES_STALE_INTERVAL = 60_000;

export const uploadedDateOptions = [
  {
    value: dateToUTCWithoutMS(subWeeks(startOfDay(new Date()), 1)),
    label: 'Past week',
  },
  {
    value: dateToUTCWithoutMS(subMonths(startOfDay(new Date()), 1)),
    label: 'Past month',
  },
  {
    value: dateToUTCWithoutMS(subMonths(startOfDay(new Date()), 3)),
    label: 'Past 3 months',
  },
  {
    value: dateToUTCWithoutMS(subYears(startOfDay(new Date()), 1)),
    label: 'Past year',
  },
];

export const actionedDateOptions = [...uploadedDateOptions];

const INVOICE_TYPE_LABELS: Record<string, string> = {
  rebill: 'Consolidation',
  'new order': 'New order',
};

const INVOICE_TYPE_STYLES: Record<string, string> = {
  rebill: 'bg-green-300 bg-opacity-[0.16] text-green-300',
  'new order': 'bg-orange-300 bg-opacity-[0.16] text-orange-300',
};

export const invoiceTypeFilterOptions = Object.entries(INVOICE_TYPE_LABELS).map(
  ([value, label]) => ({ label, value }),
);

const actionedInvoicesBaseColumns: ColumnDef<ActionedInvoiceItem>[] = [
  {
    id: 'invoice_type',
    header: 'Action taken',
    accessorKey: 'invoice_type',
    cell: (row) => {
      const type = (row.getValue() as string)?.toLowerCase();
      const label =
        INVOICE_TYPE_LABELS[type] ||
        type.charAt(0).toUpperCase() + type.slice(1);
      const style = INVOICE_TYPE_STYLES[type];

      return (
        <Tag
          content={{
            type: 'label',
            label,
          }}
          status="custom"
          className={clsx(style)}
        />
      );
    },
  },
  { id: 'requester', header: 'Uploaded by', accessorKey: 'requester' },
  {
    id: 'vendor',
    header: 'Invoice vendor',
    accessorKey: 'vendor',
  },
  {
    id: 'vendor_account_number',
    header: 'Vendor acct #',
    accessorKey: 'vendor_account_number',
  },
  {
    id: 'address',
    header: 'Address',
    accessorKey: 'address1',
    cell: (row) => (
      <StackedCell>
        <StackedCellMainContent>
          {row.row.original.address1}
        </StackedCellMainContent>
        <StackedCellSubtitle>
          {row.row.original.city}, {row.row.original.state},
          {row.row.original.zip_code}
        </StackedCellSubtitle>
      </StackedCell>
    ),
  },
  // {
  //   id: 'granite_account_number',
  //   header: 'Granite acct #',
  //   accessorKey: 'granite_account_number',
  //   cell: (row) => <TicketIdBadge>{row.getValue() as string}</TicketIdBadge>,
  // },
  {
    id: 'service_type',
    header: 'Service type',
    accessorKey: 'service_type',
  },
  {
    id: 'upload_date',
    header: 'Upload date',
    accessorKey: 'upload_date',
    cell: ({ getValue }) => {
      const date = getValue() as string | undefined;

      if (!date) {
        return <span className="text-sm text-content-base-default">N/A</span>;
      }

      const formattedDate = format(
        typeof date === 'string' ? new Date(date) : date,
        'MM/dd/yyyy',
      );
      return formattedDate;
    },
  },
  {
    id: 'actioned_date',
    header: 'Actioned date',
    accessorKey: 'actioned_date',
    cell: ({ getValue }) => {
      const date = getValue() as string | undefined;

      if (!date) {
        return <span className="text-sm text-content-base-default">N/A</span>;
      }

      const formattedDate = format(
        typeof date === 'string' ? new Date(date) : date,
        'MM/dd/yyyy',
      );
      return formattedDate;
    },
  },
  { id: 'parent_name', header: 'Parent name', accessorKey: 'parent_name' },
  { id: 'parent_number', header: 'Parent #', accessorKey: 'parent_number' },
  {
    id: 'invoice_filename',
    header: 'Invoice name',
    accessorKey: 'invoice_filename',
    cell: ({ getValue, row }) => {
      const fileName = getValue() as string | undefined;
      const invoiceId = row.original.invoice_id;

      if (!fileName || !invoiceId) {
        return <>{fileName ?? ''}</>;
      }

      return (
        <InvoiceDownloadButton invoiceId={invoiceId} fileName={fileName} />
      );
    },
  },
  {
    id: 'service_mrc',
    header: 'MRC',
    accessorKey: 'service_mrc',
    cell: ({ getValue }) => {
      const mrc = getValue() as number | undefined;

      if (!mrc) {
        return <span className="text-sm text-content-base-default">N/A</span>;
      }

      const num = typeof mrc === 'string' ? parseFloat(mrc) : mrc ?? 0;
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    },
  },
  {
    id: 'bandwidth',
    header: 'Bandwidth',
    accessorKey: 'bandwidth',
    cell: ({ getValue }) => {
      const bandwidth = getValue() as string | undefined;

      if (!bandwidth) {
        return <span className="text-sm text-content-base-default">N/A</span>;
      }

      return formatBandwidth(bandwidth);
    },
  },
];

const NonGraniteTables = ({
  activeTab,
  setActiveTab,
  selectedTableView,
  setTableView,
}: {
  activeTab: ViewType;
  setActiveTab: (value: ViewType) => void;
  setTableView: (selectedTableView: OptionType) => void;
  selectedTableView: OptionType;
}) => {
  const { data: staticData } = useInventoryStaticData();

  const [search, setSearch] = useQueryState('search', {
    defaultValue: '',
    getParam: (value) => value,
    setParam: (value) => value,
  });

  const [pageSize, setPageSize] = useState<number>();

  const { getQueryParam, setQueryParam, resetQueryParams } = useQueryParams({
    invoice_type: {
      defaultValue: [] as MultiValue<OptionType>,
      getParam: (value) =>
        value ? value.split(',').map((v) => ({ label: v, value: v })) : [],
      setParam: (value) => value.map(({ value }) => value).join(','),
    },
    vendors: {
      defaultValue: [] as MultiValue<OptionType>,
      setParam: (value) =>
        Array.isArray(value)
          ? encodeURIComponent(value.map(({ value }) => value).join('|'))
          : '',
      getParam: (value) => {
        try {
          return decodeURIComponent(value)
            .split('|')
            .map((v) => ({
              label: v.split(',')[0],
              value: v,
            }));
        } catch (e) {
          return [];
        }
      },
    },
  });

  const paginationChanged = (page: number) => {
    setPageSize(page);
    setCookie('paginationSizeActionedInvoices', page.toString(), 365);
  };

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'address', desc: false }],
  });

  const [invoiceTypeFilter, setInvoiceTypeFilter] = useQueryState(
    'invoice_type',
    {
      defaultValue: null as SingleValue<OptionType>,
      getParam: (value) => ({ label: value, value: value }),
      setParam: (value) => (value ? value.value : ''),
    },
  );

  const {
    queryParamFilter: vendorFilter,
    clearFilter: clearVendorFilter,
    ...vendorFilterProps
  } = useFilterForTable({
    queryParamKey: 'vendors',
    defaultValue: getQueryParam('vendors'),
    onAfterChange: (values) => setQueryParam('vendors', values),
  });

  const [uploadedDateFilter, setUploadedDateFilter] = useQueryState(
    'upload_date',
    {
      defaultValue: null as SingleValue<OptionType>,
      getParam: (value) => ({ label: value, value: value }),
      setParam: (value) => (value ? value.value : ''),
    },
  );
  const [actionedDateFilter, setActionedDateFilter] = useQueryState(
    'actioned_date',
    {
      defaultValue: null as SingleValue<OptionType>,
      getParam: (value) => ({ label: value, value: value }),
      setParam: (value) => (value ? value.value : ''),
    },
  );

  const buildIActionedInvoicesQueryParams = useCallback(
    (args?: ActionedInvoicesFilterSearchParams) => ({
      ...(search ? { search } : {}),
      ...sortingQueryParams,
      ...(invoiceTypeFilter ? { invoice_type: invoiceTypeFilter?.value } : {}),
      ...vendorFilter,
      ...(uploadedDateFilter
        ? {
            upload_date_from: uploadedDateFilter.value,
            upload_date_to: dateToUTCWithoutMS(startOfDay(new Date())),
          }
        : {}),
      ...(actionedDateFilter
        ? {
            actioned_date_from: actionedDateFilter.value,
            actioned_date_to: dateToUTCWithoutMS(startOfDay(new Date())),
          }
        : {}),
      ...args,
    }),
    [
      actionedDateFilter,
      invoiceTypeFilter,
      search,
      sortingQueryParams,
      uploadedDateFilter,
      vendorFilter,
    ],
  );

  const getActionedInvoicesIndexFn = useCallback(
    (args?: ActionedInvoicesFilterSearchParams) => {
      return getActionedInvoicesIndex(buildIActionedInvoicesQueryParams(args));
    },
    [buildIActionedInvoicesQueryParams],
  );

  const { data: tablePageData, ...paginatedTableProps } = usePaginatedTable(
    getActionedInvoicesIndexFn,
    {
      search,
      sortingQueryParams,
      invoiceTypeFilter,
      vendorFilter,
      uploadedDateFilter,
      actionedDateFilter,
    },
    [
      'actioned-invoices-index-table',
      search,
      sortingQueryParams,
      invoiceTypeFilter,
      vendorFilter,
      uploadedDateFilter,
      actionedDateFilter,
    ],
    {
      refetchOnMount: true,
      staleTime: SERVICES_STALE_INTERVAL,
    },
  );

  const tabs = [
    {
      name: 'all',
      title: 'All',
      onClick: () => {
        setActiveTab('all');
      },
    },
    {
      name: 'active',
      title: 'Active',
      onClick: () => {
        setActiveTab('active');
      },
    },
    {
      name: 'deactivated',
      title: 'Deactivated',
      onClick: () => {
        setActiveTab('deactivated');
      },
    },
    {
      name: 'non-granite',
      title:
        activeTab === 'non-granite' &&
        !paginatedTableProps.isFetchingData &&
        !paginatedTableProps.isFetchingError &&
        paginatedTableProps.itemCount > 0
          ? `Non-Granite (${paginatedTableProps.itemCount})`
          : 'Non-Granite',
      onClick: () => {
        setActiveTab('non-granite');
      },
    },
  ];

  const defaultActiveTab = tabs.findIndex((tab) => tab.name === activeTab);

  const {
    columns,
    customizeProps,
    customizeFirstColumnMobileClass,
    hasFilterByPreference,
  } = useCustomizeTHead({
    columns: actionedInvoicesBaseColumns,
    tableName: 'inventory-invoices-service-view',
  });

  const clearAllFilters = useCallback(() => {
    setInvoiceTypeFilter(null);
    clearVendorFilter();
    setSearch('');
    setUploadedDateFilter(null);
    setActionedDateFilter(null);
    resetQueryParams();
  }, [
    setInvoiceTypeFilter,
    clearVendorFilter,
    setSearch,
    setUploadedDateFilter,
    setActionedDateFilter,
    resetQueryParams,
  ]);

  return (
    <div className="product-tour-inventory-my-services-table flex w-full flex-col items-start justify-start">
      <TableTitle>My services</TableTitle>
      <div className="relative mb-5 w-full">
        <Tabs tabs={tabs} defaultActiveTab={defaultActiveTab} />
      </div>
      <div className="mb-4 flex w-full flex-wrap items-start justify-between gap-4">
        <div className="w-full sm:flex-1">
          <Searchbar
            placeholder="Search account #, name, address, or service ID"
            clearAllValues={search === ''}
            onQueryClear={() => setSearch('')}
            onSearch={(query: string) => {
              setSearch(query);
            }}
            defaultQuery={search}
          />
        </div>
        <SwitchTable
          setTableView={setTableView}
          selectedTableView={selectedTableView}
        />
        <Customize {...customizeProps} />
        <Filters
          clearFilters={clearAllFilters}
          clearFilterClassName="col-span-full md:col-span-1 w-full"
          className="grid w-full grid-cols-4 gap-4 md:flex"
        >
          <GraniteSelect
            options={invoiceTypeFilterOptions}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('invoice_type') && '!hidden',
            )}
            placeholder="Action taken"
            controlShouldRenderValue={false}
            isSearchable={false}
            onChange={setInvoiceTypeFilter}
            value={invoiceTypeFilter}
          />
          <GraniteSelect
            options={staticData?.invoice_vendors?.map((item: string) => ({
              label: toSentenceCase(item),
              value: item,
            }))}
            isMulti
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('vendor') && '!hidden',
            )}
            placeholder="Invoice vendor"
            controlShouldRenderValue={false}
            isSearchable={false}
            {...vendorFilterProps}
          />

          <GraniteSelect
            options={uploadedDateOptions}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('upload_date') && '!hidden',
            )}
            placeholder="Uploaded date"
            isClearable={false}
            isSearchable={false}
            controlShouldRenderValue={false}
            onChange={setUploadedDateFilter}
            value={uploadedDateFilter}
          />
          <GraniteSelect
            options={actionedDateOptions}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('actioned_date') && '!hidden',
            )}
            placeholder="Actioned date"
            isClearable={false}
            isSearchable={false}
            controlShouldRenderValue={false}
            onChange={setActionedDateFilter}
            value={actionedDateFilter}
          />
        </Filters>
      </div>
      <div className="mb-12 flex flex-wrap gap-4">
        {invoiceTypeFilter?.value && (
          <Chip
            label={invoiceTypeFilter.label}
            onDelete={() => {
              setInvoiceTypeFilter(null);
            }}
          />
        )}
        {vendorFilterProps.value.map((sf) => (
          <Chip
            key={sf.value}
            label={sf.label}
            onDelete={() =>
              vendorFilterProps.onChange(
                vendorFilterProps.value.filter(
                  (option) => option.value !== sf.value,
                ),
              )
            }
          />
        ))}
        {uploadedDateFilter?.value && (
          <Chip
            label={uploadedDateFilter.label}
            onDelete={() => {
              setUploadedDateFilter(null);
            }}
          />
        )}
        {actionedDateFilter?.value && (
          <Chip
            label={actionedDateFilter.label}
            onDelete={() => {
              setActionedDateFilter(null);
            }}
          />
        )}
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-12">
        <UploadWidgetTable />
        {tablePageData?.length > 0 && (
          <ServerPaginatedTable
            data={tablePageData ?? []}
            columns={columns}
            title="Actioned Invoices"
            sortingState={sortingState}
            onSortingChange={onSortingChange}
            emptyDataElement={<React.Fragment />}
            tableContainerClassName={customizeFirstColumnMobileClass}
            paginationSizeStored={pageSize}
            paginationChanged={paginationChanged}
            {...paginatedTableProps}
          />
        )}
      </div>
    </div>
  );
};

export default NonGraniteTables;
